import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ZOOM_MIN, ZOOM_MAX } from './framing';

// BARRE FINE DE CADRAGE (correctif 5, 2026-10-16) — affichage PROGRESSIF.
//
//   Repos       : rien (juste le curseur « main » au survol de la photo).
//   Selectionnee: cette barre, placee AU-DESSUS de la photo (jamais dessus) :
//                 [ Remplir | Entiere ] · zoom · Remplacer · ⋯ (Retirer).
//   Pendant le glissement seulement : bande « Pli » (voir SlotFramingLayer).
//
// Elle est posee dans <body> en position fixe, recalee a chaque image sur la photo : une page qui tourne, un
// defilement ou un zoom du navigateur ne la decalent pas, et le bord de la page ne la coupe jamais. Les gestes
// (glisser, molette, pincement, double-clic) sont ceux de useFramingGestures.js, inchanges.
//
// Plus aucune bulle native (attribut title) : elles debordaient de l'ecran. Une SEULE info-bulle de premier
// usage (« Glissez la photo pour la recadrer »), fermable, qui ne revient plus.
const HINT_KEY = 'atelierFramingHintSeen';
const readHintSeen = () => { try { return window.localStorage.getItem(HINT_KEY) === '1'; } catch (_error) { return false; } };
const writeHintSeen = () => { try { window.localStorage.setItem(HINT_KEY, '1'); } catch (_error) { /* stockage indisponible */ } };

function PhotoFramingBar({ fit, zoom, onFit, onZoom, onReplace, onRemove, faceWarning, anchorElement }) {
  const { t } = useTranslation('atelier');
  const barRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hintVisible, setHintVisible] = useState(() => !readHintSeen());
  const stop = (event) => event.stopPropagation();

  // Recalage sur la photo a chaque image : au-dessus, centre ; a defaut de place, juste en dessous.
  useLayoutEffect(() => {
    if (!anchorElement) return undefined;
    let frame = 0;
    const tick = () => {
      const photo = anchorElement.getBoundingClientRect();
      const bar = barRef.current ? barRef.current.getBoundingClientRect() : { width: 0, height: 44 };
      const left = Math.max(8, Math.min(window.innerWidth - bar.width - 8, photo.left + photo.width / 2 - bar.width / 2));
      let top = photo.top - bar.height - 8;
      if (top < 8) top = photo.bottom + 8 + bar.height < window.innerHeight ? photo.bottom + 8 : 8;
      const next = { left: Math.round(left), top: Math.round(top) };
      setPosition((previous) => (previous && previous.left === next.left && previous.top === next.top ? previous : next));
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [anchorElement]);

  // Le menu « ⋯ » se ferme au clic ailleurs.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (event) => { if (!event.target.closest || !event.target.closest('.atelier-framing-more')) setMenuOpen(false); };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [menuOpen]);

  const dismissHint = () => { writeHintSeen(); setHintVisible(false); };

  // Premier glissement : l'info-bulle a fait son travail, elle ne reviendra plus.
  useEffect(() => {
    if (!anchorElement || typeof MutationObserver === 'undefined') return undefined;
    const observer = new MutationObserver(() => { if (anchorElement.classList.contains('is-framing-drag')) { writeHintSeen(); setHintVisible(false); } });
    observer.observe(anchorElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [anchorElement]);

  const bar = (
    <div
      ref={barRef}
      className="atelier-framing-bar"
      role="toolbar"
      aria-label={t('framing.toolbarLabel')}
      style={{ left: position ? position.left : -9999, top: position ? position.top : -9999 }}
      onClick={stop}
      onDoubleClick={stop}
      onPointerDown={stop}
    >
      <div className="atelier-framing-seg" role="group" aria-label={t('framing.fitGroup')}>
        <button type="button" aria-pressed={fit === 'cover'} onClick={() => onFit('cover')}>{t('framing.fill')}</button>
        <button type="button" aria-pressed={fit === 'contain'} onClick={() => onFit('contain')}>{t('framing.whole')}</button>
      </div>
      {fit === 'cover' && (
        <>
          <span className="atelier-framing-sep" aria-hidden="true" />
          <input
            className="atelier-framing-zoom"
            type="range"
            min={ZOOM_MIN}
            max={ZOOM_MAX}
            step="0.05"
            value={zoom}
            aria-label={t('selection.zoom')}
            onChange={(event) => onZoom(Number(event.target.value))}
          />
        </>
      )}
      <span className="atelier-framing-sep" aria-hidden="true" />
      {onReplace && <button type="button" className="atelier-framing-action" onClick={onReplace}>{t('framing.replace')}</button>}
      {onRemove && (
        <span className="atelier-framing-more">
          <button
            type="button"
            className="atelier-framing-action"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={t('framing.more')}
            onClick={() => setMenuOpen((open) => !open)}
          >
            ⋯
          </button>
          {menuOpen && (
            <span className="atelier-framing-menu" role="menu">
              <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenuOpen(false); onRemove(); }}>
                {t('framing.remove')}
              </button>
            </span>
          )}
        </span>
      )}
      {faceWarning && <span className="atelier-framing-fold" role="status">{t('framing.faceNearFold')}</span>}
      {hintVisible && (
        <span className="atelier-framing-hint" role="note">
          {t('framing.firstUseHint')}
          <button type="button" aria-label={t('common.close')} onClick={dismissHint}>×</button>
        </span>
      )}
    </div>
  );

  return typeof document === 'undefined' ? null : createPortal(bar, document.body);
}

export default PhotoFramingBar;
